/**
 * Execution Adapter — SIMULATION ONLY
 *
 * Two-step flow: previewTrade() → executeSimulationTrade()
 * ❌ NO real broker API calls. ❌ NO Alpaca. ❌ NO account modification.
 *
 * v2 — strict response validation, malformed rejection, tightened error classification.
 */

import { apiV1Base } from '../lib/apiConfig';
import type {
  TradePreviewRequest,
  TradePreviewData,
  TradeExecuteData,
  TradeGatewayResponse,
  NormalizedPreview,
  NormalizedExecution,
  ExecutionErrorType,
} from '../types/execution';

// ═══════════════════════════════════════════════════════════════════════
//  CONFIG
// ═══════════════════════════════════════════════════════════════════════

function getEnv(key: string, fallback: string): string {
  try {
    return (import.meta as any).env?.[key] ?? fallback;
  } catch {
    return fallback;
  }
}

const GATEWAY_BASE = apiV1Base();
const API_KEY = getEnv('VITE_API_KEY', 'adversyn-dev-key-2026');
const TIMEOUT_MS = 10_000;

// ═══════════════════════════════════════════════════════════════════════
//  HTTP HELPER
// ═══════════════════════════════════════════════════════════════════════

async function gatewayPost<T>(endpoint: string, body: unknown): Promise<TradeGatewayResponse<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(`${GATEWAY_BASE}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    let json: TradeGatewayResponse<T>;
    try {
      json = await response.json();
    } catch {
      throw new ExecutionApiError(
        `Invalid JSON from gateway (HTTP ${response.status})`,
        'PARSE_ERROR',
        response.status,
      );
    }

    if (!response.ok || !json.success) {
      throw new ExecutionApiError(
        json?.error?.message ?? `HTTP ${response.status}`,
        json?.error?.code ?? 'API_ERROR',
        response.status,
      );
    }

    return json;
  } finally {
    clearTimeout(timer);
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  ERROR
// ═══════════════════════════════════════════════════════════════════════

export class ExecutionApiError extends Error {
  code: string;
  statusCode: number;
  constructor(message: string, code: string, statusCode: number) {
    super(message);
    this.name = 'ExecutionApiError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

export function classifyExecutionError(error: unknown): ExecutionErrorType {
  if (error instanceof ExecutionApiError) {
    if (error.code === 'PARSE_ERROR') return 'validation';
    if (error.code === 'VALIDATION_ERROR') return 'validation';
    if (error.code === 'MALFORMED_RESPONSE') return 'validation';
    if (error.statusCode === 422) return 'validation';
    if (error.statusCode === 429) return 'server';
    if (error.statusCode >= 500) return 'server';
    if (error.statusCode === 401 || error.statusCode === 403) return 'server';
    return 'server';
  }
  if (error instanceof DOMException && error.name === 'AbortError') return 'timeout';
  if (error instanceof TypeError && error.message.includes('fetch')) return 'network';
  if (error instanceof TypeError && error.message.includes('Failed')) return 'network';
  return 'unknown';
}

// ═══════════════════════════════════════════════════════════════════════
//  STRICT VALIDATION — reject malformed responses
// ═══════════════════════════════════════════════════════════════════════

function validatePreviewData(data: unknown): data is TradePreviewData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  // Must have preview_id
  if (typeof d.preview_id !== 'string' || d.preview_id.length === 0) return false;
  // Price fields must be numbers if present
  if (d.estimated_price !== undefined && typeof d.estimated_price !== 'number') return false;
  if (d.quantity !== undefined && typeof d.quantity !== 'number') return false;
  if (d.estimated_total !== undefined && typeof d.estimated_total !== 'number') return false;
  if (d.fees_estimate !== undefined && typeof d.fees_estimate !== 'number') return false;
  return true;
}

function validateExecuteData(data: unknown): data is TradeExecuteData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  // Must have order_id and status
  if (typeof d.order_id !== 'string' || d.order_id.length === 0) return false;
  if (typeof d.status !== 'string' || d.status.length === 0) return false;
  // Price must be number if present
  if (d.filled_price !== undefined && typeof d.filled_price !== 'number') return false;
  if (d.quantity !== undefined && typeof d.quantity !== 'number') return false;
  return true;
}

// ═══════════════════════════════════════════════════════════════════════
//  NORMALIZE
// ═══════════════════════════════════════════════════════════════════════

export function normalizePreviewResponse(data: TradePreviewData): NormalizedPreview {
  return {
    previewId: data.preview_id ?? '',
    symbol: data.symbol ?? '',
    side: (data.side as NormalizedPreview['side']) ?? 'buy',
    orderType: (data.order_type as NormalizedPreview['orderType']) ?? 'market',
    quantity: data.quantity ?? 0,
    estimatedPrice: data.estimated_price ?? 0,
    estimatedTotal: data.estimated_total ?? 0,
    feesEstimate: data.fees_estimate ?? 0,
    expiresAt: data.expires_at ?? '',
  };
}

export function normalizeExecutionResponse(data: TradeExecuteData): NormalizedExecution {
  const rawStatus = data.status ?? 'rejected';
  let status: NormalizedExecution['status'] = 'rejected';
  if (rawStatus.includes('filled')) status = 'filled';
  else if (rawStatus === 'pending') status = 'pending';

  return {
    executionId: data.order_id ?? `exec-${Date.now()}`,
    status,
    price: data.filled_price ?? 0,
    quantity: data.quantity ?? 0,
    timestamp: new Date().toISOString(),
    message: data.message ?? 'No execution details available.',
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  PUBLIC API — SIMULATION ONLY
// ═══════════════════════════════════════════════════════════════════════

export interface PreviewResult {
  success: boolean;
  preview: NormalizedPreview | null;
  error: string | null;
  errorType: ExecutionErrorType | null;
}

export async function previewTrade(request: TradePreviewRequest): Promise<PreviewResult> {
  // ── Input validation ──
  if (!request.symbol || request.symbol.trim().length === 0) {
    return { success: false, preview: null, error: 'Symbol is required', errorType: 'validation' };
  }
  if (!request.quantity || request.quantity <= 0) {
    return { success: false, preview: null, error: 'Quantity must be positive', errorType: 'validation' };
  }
  if (!['buy', 'sell'].includes(request.side)) {
    return { success: false, preview: null, error: 'Side must be buy or sell', errorType: 'validation' };
  }

  try {
    const response = await gatewayPost<TradePreviewData>('/trade/preview', request);

    // ── Strict response validation ──
    if (!validatePreviewData(response.data)) {
      return {
        success: false,
        preview: null,
        error: 'Gateway returned malformed preview data',
        errorType: 'validation',
      };
    }

    return {
      success: true,
      preview: normalizePreviewResponse(response.data),
      error: null,
      errorType: null,
    };
  } catch (err) {
    return {
      success: false,
      preview: null,
      error: err instanceof Error ? err.message : 'Preview failed',
      errorType: classifyExecutionError(err),
    };
  }
}

export interface ExecutionResult {
  success: boolean;
  execution: NormalizedExecution | null;
  error: string | null;
  errorType: ExecutionErrorType | null;
}

export async function executeSimulationTrade(previewId: string): Promise<ExecutionResult> {
  // ── Input validation ──
  if (!previewId || previewId.trim().length === 0) {
    return { success: false, execution: null, error: 'Preview ID is required', errorType: 'validation' };
  }

  try {
    const response = await gatewayPost<TradeExecuteData>('/trade/execute', {
      preview_id: previewId,
      confirm: true,
    });

    // ── Strict response validation ──
    if (!validateExecuteData(response.data)) {
      return {
        success: false,
        execution: null,
        error: 'Gateway returned malformed execution data',
        errorType: 'validation',
      };
    }

    const normalized = normalizeExecutionResponse(response.data);

    // Check for rejection at data level
    if (normalized.status === 'rejected') {
      return {
        success: false,
        execution: normalized,
        error: normalized.message,
        errorType: 'rejected',
      };
    }

    return {
      success: true,
      execution: normalized,
      error: null,
      errorType: null,
    };
  } catch (err) {
    return {
      success: false,
      execution: null,
      error: err instanceof Error ? err.message : 'Execution failed',
      errorType: classifyExecutionError(err),
    };
  }
}
